import React, { useEffect, useState, createElement, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Copy, FileText, Clock, AlertCircle, RefreshCw, ImageIcon, Link as LinkIcon, Download, Share2, Calendar, User, Heart, MessageSquare, Repeat2, Tag, Monitor, Film, Info, Languages, Layers, Code, ExternalLink, Play, Quote, BrainCircuit, ChevronDown, ChevronUp, Twitter } from 'lucide-react';
import { getTaskStatus, getTranscript, parseTranscript, getTranscriptWithFormat, getSimilarTranscriptions, getPublicTranscriptPageUrl } from '../lib/api';
import { TranscriptionResponse, TranscriptionResult, TranscriptFormat } from '../lib/types';
import { TranscriptionCard } from '../components/transcription/TranscriptionCard';
import { ShareTranscript } from '../components/transcription/ShareTranscript';
import { MetaTags } from '../components/MetaTags';
import { ResearchTools } from '../components/research/ResearchTools';
export function TranscriptionDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [copied, setCopied] = useState(false);
  const [task, setTask] = useState<TranscriptionResponse | null>(null);
  const [transcript, setTranscript] = useState<TranscriptionResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [similarTranscriptions, setSimilarTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [quoteCopied, setQuoteCopied] = useState(false);
  // Helper function to parse TLDR from string to array
  const parseTLDR = (tldrData?: string | string[]): string[] => {
    if (!tldrData) return [];
    // If tldrData is already an array, return it
    if (Array.isArray(tldrData)) {
      return tldrData;
    }
    // Otherwise try to parse it as a JSON string
    try {
      return JSON.parse(tldrData);
    } catch {
      return [];
    }
  };
  const getThumbnailUrl = () => {
    if (task && task.status === 'completed') {
      return task.supabase_thumbnail_url || task.square_thumbnail_url || task.thumbnail_url || task.thumbnail;
    }
    return null;
  };
  const renderPlaceholder = () => {
    if (!task) return null;
    const title = task.title || 'Untitled Video';
    const platform = task.platform || 'TikTok';
    const videoId = task.video_id || task.task_id || 'Unknown';
    return <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-950 relative" data-id="element-1963">
        <div className="absolute inset-0 opacity-10" data-id="element-1964">
          <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-primary/20" data-id="element-1965"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 rounded-full bg-primary/30" data-id="element-1966"></div>
        </div>
        <div className="z-10 flex flex-col items-center justify-center p-6 text-center" data-id="element-1967">
          <Film size={36} className="text-gray-400 mb-4" data-id="element-1968" />
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4" data-id="element-1969">
            <span className="text-2xl font-bold text-white" data-id="element-1970">
              {title.substring(0, 2).toUpperCase()}
            </span>
          </div>
          <h3 className="text-white/90 text-lg font-medium mb-2 max-w-xs" data-id="element-1971">
            {title}
          </h3>
          <p className="text-white/60 text-sm mb-1" data-id="element-1972">{platform} Transcript</p>
          <p className="text-white/40 text-xs" data-id="element-1973">
            ID: {videoId.substring(0, 12)}...
          </p>
          {task.duration && <div className="mt-4 bg-white/10 px-3 py-1 rounded-full flex items-center" data-id="element-1974">
              <Clock size={14} className="text-primary mr-2" data-id="element-1975" />
              <span className="text-white/70 text-sm" data-id="element-1976">
                {formatDuration(task.duration)}
              </span>
            </div>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/6 bg-gradient-to-t from-black/40 to-transparent" data-id="element-1977"></div>
      </div>;
  };
  const getMetaTitle = () => {
    if (!task) return 'Transcript | ScribeTok';
    return `TikTok Transcript: "${task.title || 'Untitled Video'}" | ScribeTok`;
  };
  const getMetaDescription = () => {
    if (!task) return 'View the full transcript of this TikTok video on ScribeTok.';
    // Use the quote for the description if available
    if (task.quote) {
      return `"${task.quote}" - See the full transcript for this TikTok video on ScribeTok.`;
    }
    let description = `See the full transcript for this TikTok video`;
    // Add engagement metrics if available
    if (task.like_count || task.comment_count) {
      description += ' (';
      if (task.like_count) description += `${task.like_count.toLocaleString()} likes`;
      if (task.like_count && task.comment_count) description += ', ';
      if (task.comment_count) description += `${task.comment_count.toLocaleString()} comments`;
      description += ')';
    }
    description += ' on ScribeTok.';
    // Add a snippet of the transcript if available
    if (transcript && transcript.text) {
      const snippet = transcript.text.substring(0, 100).trim();
      description += ` "${snippet}${transcript.text.length > 100 ? '...' : ''}"`;
    }
    return description;
  };
  const getMetaImageUrl = () => {
    if (!task) return undefined;
    return getThumbnailUrl() || undefined;
  };
  const getMetaUrl = () => {
    if (!id) return window.location.href;
    return getPublicTranscriptPageUrl(id);
  };
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        setError(null);
        const taskData = await getTaskStatus(id);
        setTask(taskData);
        if (taskData.status === 'completed') {
          try {
            // Always get transcript in text format
            const rawTranscript = (await getTranscriptWithFormat(id, 'text')) as string;
            const parsedTranscript = parseTranscript(rawTranscript);
            setTranscript(parsedTranscript);
            // Get similar transcriptions
            const similar = await getSimilarTranscriptions(id, 4);
            setSimilarTranscriptions(similar);
          } catch (err) {
            console.error('Error fetching transcript data:', err);
          }
          setIsPolling(false);
          setProcessingStartTime(null);
        } else if (taskData.status === 'processing' || taskData.status === 'pending') {
          setIsPolling(true);
          if (!processingStartTime) {
            setProcessingStartTime(Date.now());
          }
        } else if (taskData.status === 'failed') {
          setError(taskData.error || 'Transcription failed');
          setIsPolling(false);
          setProcessingStartTime(null);
        }
      } catch (err) {
        console.error('Error fetching task data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transcription');
        setIsPolling(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTaskData();
    let intervalId: NodeJS.Timeout;
    if (isPolling) {
      intervalId = setInterval(() => {
        fetchTaskData();
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [id, isPolling, processingStartTime]);
  useEffect(() => {
    if (!processingStartTime) return;
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - processingStartTime) / 1000); // elapsed time in seconds
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      setElapsedTime(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [processingStartTime]);
  const handleCopyTranscript = () => {
    if (!transcript) return;
    navigator.clipboard.writeText(transcript.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyQuote = () => {
    if (!task || !task.quote) return;
    navigator.clipboard.writeText(`"${task.quote}" - ${task.title || 'TikTok Video'}`);
    setQuoteCopied(true);
    setTimeout(() => setQuoteCopied(false), 2000);
  };
  const handleShareQuote = () => {
    if (!task || !task.quote) return;
    const shareText = `"${task.quote}" - ${task.title || 'TikTok Video'}`;
    const shareUrl = getPublicTranscriptPageUrl(task.task_id);
    if (navigator.share) {
      navigator.share({
        text: shareText,
        url: shareUrl
      }).catch(err => {
        console.error('Share failed:', err);
      });
    } else {
      // Fallback if Web Share API is not available
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };
  const handleDownload = () => {
    if (!transcript || !task) return;
    const content = transcript.text;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${task.video_id || task.task_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleDownloadSrt = () => {
    if (!task) return;
    getTranscriptWithFormat(task.task_id, 'srt').then(content => {
      const blob = new Blob([content as string], {
        type: 'text/srt'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript-${task.video_id || task.task_id}.srt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(err => console.error('Error downloading SRT:', err));
  };
  const handleSharePublicLink = () => {
    if (!task) return;
    const publicUrl = getPublicTranscriptPageUrl(task.task_id);
    navigator.clipboard.writeText(publicUrl);
    alert('Public link copied to clipboard!');
  };
  const handleShare = () => {
    if (navigator.share && transcript && task) {
      navigator.share({
        title: task?.title || 'Video Transcript',
        text: transcript.text.substring(0, 100) + '...',
        url: getPublicTranscriptPageUrl(task.task_id)
      }).catch(err => {
        console.error('Share failed:', err);
        setShowShareOptions(!showShareOptions);
      });
    } else {
      setShowShareOptions(!showShareOptions);
    }
  };
  const handleRetry = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const taskData = await getTaskStatus(id);
      setTask(taskData);
      if (taskData.status === 'failed') {
        setError('Retry not implemented in demo. In a real app, this would restart the transcription.');
      }
    } catch (err) {
      console.error('Error retrying task:', err);
      setError(err instanceof Error ? err.message : 'Failed to retry transcription');
    } finally {
      setIsLoading(false);
    }
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins < 1) {
      return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    // Handle YYYYMMDD format
    if (/^\d{8}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return new Date(`${year}-${month}-${day}`).toLocaleDateString();
    }
    // Handle regular date strings
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };
  if (isLoading) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-1978">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-1979">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-1980">
            <Button variant="ghost" size="sm" className="h-9 px-2 md:h-9 md:px-3" data-id="element-1981">
              <ArrowLeft size={16} className="mr-1 md:mr-2" data-id="element-1982" />
              <span className="hidden sm:inline" data-id="element-1983">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-1984">
            Loading Transcription...
          </h1>
        </div>
        <Card className="p-8 md:p-12 flex justify-center items-center" data-id="element-1985">
          <div className="flex flex-col items-center" data-id="element-1986">
            <RefreshCw size={32} className="animate-spin text-primary mb-4" data-id="element-1987" />
            <p className="text-muted-foreground" data-id="element-1988">
              Loading transcription data...
            </p>
          </div>
        </Card>
      </div>;
  }
  if (error || !task) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-1989">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-1990">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-1991">
            <Button variant="ghost" size="sm" className="h-9 px-2 md:h-9 md:px-3" data-id="element-1992">
              <ArrowLeft size={16} className="mr-1 md:mr-2" data-id="element-1993" />
              <span className="hidden sm:inline" data-id="element-1994">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-1995">Error</h1>
        </div>
        <Card className="p-8 md:p-12 border-destructive/20 bg-destructive/5" data-id="element-1996">
          <div className="flex flex-col items-center text-center" data-id="element-1997">
            <AlertCircle size={32} className="text-destructive mb-4" data-id="element-1998" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-1999">
              Failed to Load Transcription
            </h2>
            <p className="text-muted-foreground mb-6" data-id="element-2000">
              {error || 'Transcription not found or an error occurred'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2001">
              <Link to="/app/transcriptions" data-id="element-2002">
                <Button variant="outline" className="w-full sm:w-auto" data-id="element-2003">
                  Back to Transcriptions
                </Button>
              </Link>
              <Button onClick={handleRetry} className="w-full sm:w-auto" data-id="element-2004">
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>;
  }
  if (task && (task.status === 'processing' || task.status === 'pending')) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2005">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2006">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2007">
            <Button variant="ghost" size="sm" className="h-9 px-2 md:h-9 md:px-3" data-id="element-2008">
              <ArrowLeft size={16} className="mr-1 md:mr-2" data-id="element-2009" />
              <span className="hidden sm:inline" data-id="element-2010">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2011">
            Processing Transcription
          </h1>
        </div>
        <Card className="p-8 md:p-12" data-id="element-2012">
          <div className="flex flex-col items-center text-center" data-id="element-2013">
            <RefreshCw size={32} className="animate-spin text-blue-500 mb-4" data-id="element-2014" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2015">
              Your Video is Being Processed
            </h2>
            <p className="text-gray-500 mb-2" data-id="element-2016">
              This may take a few minutes depending on the length of the video.
            </p>
            {processingStartTime && <p className="text-blue-500 font-medium mb-6" data-id="element-2017">
                Processing time: {elapsedTime}
              </p>}
            <div className="w-full max-w-md mb-6" data-id="element-2018">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2019">
                <div className="h-2 bg-blue-500 rounded-full animate-pulse w-full" data-id="element-2020"></div>
              </div>
            </div>
            <Button variant="outline" onClick={() => window.location.reload()} className="flex items-center" data-id="element-2021">
              <RefreshCw size={16} className="mr-2" data-id="element-2022" />
              Refresh Status
            </Button>
          </div>
        </Card>
      </div>;
  }
  // Parse TLDR if available
  const tldrItems = task.tldr ? parseTLDR(task.tldr) : [];
  const hasInsights = task.quote || tldrItems && tldrItems.length > 0;
  return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2023">
      {task && <MetaTags title={getMetaTitle()} description={getMetaDescription()} imageUrl={getMetaImageUrl()} url={getMetaUrl()} data-id="element-2024" />}
      <div className="flex items-center mb-4 md:mb-6" data-id="element-2025">
        <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2026">
          <Button variant="ghost" size="sm" className="h-9 px-2 md:h-9 md:px-3 rounded-full" data-id="element-2027">
            <ArrowLeft size={16} className="mr-1 md:mr-2" data-id="element-2028" />
            <span className="hidden sm:inline" data-id="element-2029">Back</span>
          </Button>
        </Link>
        <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2030">
          {task?.title || 'Untitled Video'}
        </h1>
      </div>

      {/* Mobile Action Buttons */}
      <div className="flex gap-2 mb-4 md:hidden" data-id="element-2031">
        <Button variant="tiktok" className="flex-1 justify-center items-center rounded-full" onClick={handleCopyTranscript} disabled={!transcript} data-id="element-2032">
          <Copy size={16} className="mr-1.5" data-id="element-2033" />
          Copy
        </Button>
        <Button variant="outline" className="flex-1 justify-center items-center rounded-full" onClick={handleDownload} disabled={!transcript} data-id="element-2034">
          <Download size={16} className="mr-1.5" data-id="element-2035" />
          Download
        </Button>
        <Button variant="outline" className="flex-1 justify-center items-center rounded-full" onClick={handleShare} disabled={!transcript} data-id="element-2036">
          <Share2 size={16} className="mr-1.5" data-id="element-2037" />
          Share
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-2038">
        <div className="lg:col-span-1 space-y-4 md:space-y-6" data-id="element-2039">
          {/* Video Preview Card */}
          <Card data-id="element-2040">
            <div className="w-full h-64 md:h-72 bg-gray-800 rounded-t-xl flex items-center justify-center overflow-hidden relative" data-id="element-2041">
              {task.status === 'completed' ? <div className="relative w-full h-full" data-id="element-2042">
                  {getThumbnailUrl() ? <img src={getThumbnailUrl() || ''} alt={task.title || 'Video thumbnail'} className="w-full h-full object-contain relative z-10" style={{
                maxHeight: '100%',
                objectPosition: 'center'
              }} onError={e => {
                e.currentTarget.style.display = 'none';
                const container = e.currentTarget.parentElement;
                if (container) {
                  container.innerHTML = '';
                  const placeholderDiv = document.createElement('div');
                  placeholderDiv.className = 'w-full h-full';
                  container.appendChild(placeholderDiv);
                  // Create a styled placeholder
                  placeholderDiv.innerHTML = `
                            <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-950 relative">
                              <div class="z-10 flex flex-col items-center justify-center p-6 text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400 mb-4"><path d="M12 2v8"></path><path d="m4.93 10.93 1.41 1.41"></path><path d="M2 18h2"></path><path d="M20 18h2"></path><path d="m19.07 10.93-1.41 1.41"></path><path d="M22 22H2"></path><path d="m16 6-4 4-4-4"></path><path d="M16 18a4 4 0 0 0-8 0"></path></svg>
                                <div class="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                                  <span class="text-2xl font-bold text-white">${(task.title || 'UV').substring(0, 2).toUpperCase()}</span>
                                </div>
                                <h3 class="text-white/90 text-lg font-medium mb-2 max-w-xs">${task.title || 'Untitled Video'}</h3>
                                <p class="text-white/60 text-sm mb-1">${task.platform || 'TikTok'} Transcript</p>
                                <p class="text-white/40 text-xs">ID: ${(task.video_id || task.task_id || 'Unknown').substring(0, 12)}...</p>
                              </div>
                            </div>
                          `;
                }
              }} data-id="element-2043" /> : renderPlaceholder()}
                  {task.url && <div className="absolute inset-0 flex items-center justify-center z-20 cursor-pointer" onClick={() => {
                // Create a modal or overlay with the video player
                const videoContainer = document.createElement('div');
                videoContainer.className = 'fixed inset-0 bg-black/80 flex items-center justify-center z-50';
                videoContainer.style.backdropFilter = 'blur(5px)';
                // Create video element
                const videoElement = document.createElement('video');
                videoElement.className = 'max-w-[90%] max-h-[90vh]';
                videoElement.controls = true;
                videoElement.autoplay = true;
                videoElement.src = task.url;
                // Create close button
                const closeButton = document.createElement('button');
                closeButton.className = 'absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors';
                closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
                // Add click event to close
                closeButton.onclick = () => {
                  document.body.removeChild(videoContainer);
                };
                // Add elements to the DOM
                videoContainer.appendChild(videoElement);
                videoContainer.appendChild(closeButton);
                document.body.appendChild(videoContainer);
                // Add click event to close when clicking outside video
                videoContainer.onclick = e => {
                  if (e.target === videoContainer) {
                    document.body.removeChild(videoContainer);
                  }
                };
              }} data-id="element-2044">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 flex items-center justify-center hover:bg-primary transition-colors shadow-lg" data-id="element-2045">
                        <Play size={24} className="text-white ml-1" data-id="element-2046" />
                      </div>
                    </div>}
                </div> : <div className="flex flex-col items-center justify-center" data-id="element-2047">
                  <ImageIcon size={24} className="text-gray-400 mb-2" data-id="element-2048" />
                  <span className="text-gray-500 font-medium" data-id="element-2049">
                    {task.video_id ? `Video ID: ${task.video_id}` : 'Thumbnail not available'}
                  </span>
                </div>}
            </div>
            <CardContent className="p-4" data-id="element-2050">
              <h3 className="font-medium text-base md:text-lg mb-2 line-clamp-2" data-id="element-2051">
                {task.title || 'Untitled Video'}
              </h3>
              {/* Basic Video Info */}
              <div className="space-y-3" data-id="element-2052">
                <div className="flex justify-between text-xs md:text-sm text-gray-400" data-id="element-2053">
                  <span className="flex items-center" data-id="element-2054">
                    <Clock size={14} className="mr-1" data-id="element-2055" />
                    {task.duration ? formatDuration(task.duration) : transcript && transcript.segments.length > 0 ? `${Math.floor(transcript.segments[transcript.segments.length - 1].end / 60)}:${Math.floor(transcript.segments[transcript.segments.length - 1].end % 60).toString().padStart(2, '0')}` : '00:00'}
                  </span>
                  <span className="flex items-center" data-id="element-2056">
                    <Calendar size={14} className="mr-1" data-id="element-2057" />
                    {task.upload_date ? formatDate(task.upload_date) : new Date(task.created_at).toLocaleDateString()}
                  </span>
                </div>
                {task.platform && <div className="flex items-center text-xs text-gray-400" data-id="element-2058">
                    <Film size={14} className="mr-1" data-id="element-2059" />
                    <span className="capitalize" data-id="element-2060">{task.platform}</span>
                    {task.language && <>
                        <span className="mx-1" data-id="element-2061">•</span>
                        <Languages size={14} className="mr-1" data-id="element-2062" />
                        <span className="capitalize" data-id="element-2063">{task.language}</span>
                      </>}
                  </div>}
                {/* Video ID */}
                <p className="text-xs md:text-sm text-gray-400 break-all" data-id="element-2064">
                  ID: {task.video_id || task.task_id}
                </p>
              </div>
              {/* View Original Button */}
              <div className="mt-3 space-y-2" data-id="element-2065">
                {(task.url || task.uploader_url || task.video_id) && <a href={task.url} target="_blank" rel="noopener noreferrer" className="w-full" data-id="element-2066">
                    <Button variant="tiktok" className="w-full flex items-center justify-center text-sm" data-id="element-2067">
                      <LinkIcon size={16} className="mr-2" data-id="element-2068" />
                      View Original Video
                    </Button>
                  </a>}
                <Button variant="outline" className="w-full flex items-center justify-center text-sm" onClick={handleSharePublicLink} data-id="element-2069">
                  <ExternalLink size={16} className="mr-2" data-id="element-2070" />
                  Share Public Link
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Creator Info Card */}
          {(task.channel || task.uploader || task.channel_id) && <Card data-id="element-2071">
              <CardContent className="p-4" data-id="element-2072">
                <h3 className="font-medium mb-3 flex items-center" data-id="element-2073">
                  <User size={16} className="mr-2 text-primary" data-id="element-2074" />
                  Creator Information
                </h3>
                <div className="space-y-2" data-id="element-2075">
                  {task.uploader && <div className="flex justify-between items-center" data-id="element-2076">
                      <span className="text-sm text-gray-400" data-id="element-2077">Username</span>
                      <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2078">
                        {task.uploader}
                      </span>
                    </div>}
                  {task.channel && <div className="flex justify-between items-center" data-id="element-2079">
                      <span className="text-sm text-gray-400" data-id="element-2080">Channel</span>
                      <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2081">
                        {task.channel}
                      </span>
                    </div>}
                  {task.uploader_url && <div className="mt-2" data-id="element-2082">
                      <a href={task.uploader_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline flex items-center" data-id="element-2083">
                        <LinkIcon size={14} className="mr-1" data-id="element-2084" />
                        View Creator Profile
                      </a>
                    </div>}
                </div>
              </CardContent>
            </Card>}
          {/* Engagement Metrics Card */}
          <Card data-id="element-2085">
            <CardContent className="p-4" data-id="element-2086">
              <h3 className="font-medium mb-3 flex items-center" data-id="element-2087">
                <Heart size={16} className="mr-2 text-primary" data-id="element-2088" />
                Engagement Metrics
              </h3>
              <div className="grid grid-cols-3 gap-2 text-center" data-id="element-2089">
                <div className="p-2 bg-secondary rounded-lg" data-id="element-2090">
                  <div className="flex justify-center mb-1" data-id="element-2091">
                    <Heart size={16} className="text-primary" data-id="element-2092" />
                  </div>
                  <span className="block text-sm font-medium" data-id="element-2093">
                    {task.like_count?.toLocaleString() || '—'}
                  </span>
                  <span className="block text-xs text-gray-400" data-id="element-2094">Likes</span>
                </div>
                <div className="p-2 bg-secondary rounded-lg" data-id="element-2095">
                  <div className="flex justify-center mb-1" data-id="element-2096">
                    <MessageSquare size={16} className="text-primary" data-id="element-2097" />
                  </div>
                  <span className="block text-sm font-medium" data-id="element-2098">
                    {task.comment_count?.toLocaleString() || '—'}
                  </span>
                  <span className="block text-xs text-gray-400" data-id="element-2099">Comments</span>
                </div>
                <div className="p-2 bg-secondary rounded-lg" data-id="element-2100">
                  <div className="flex justify-center mb-1" data-id="element-2101">
                    <Repeat2 size={16} className="text-primary" data-id="element-2102" />
                  </div>
                  <span className="block text-sm font-medium" data-id="element-2103">
                    {task.repost_count?.toLocaleString() || '—'}
                  </span>
                  <span className="block text-xs text-gray-400" data-id="element-2104">Reposts</span>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Tags Card */}
          {task.auto_tags && task.auto_tags.length > 0 && <Card data-id="element-2105">
              <CardContent className="p-4" data-id="element-2106">
                <h3 className="font-medium mb-3 flex items-center" data-id="element-2107">
                  <Tag size={16} className="mr-2 text-primary" data-id="element-2108" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-2109">
                  {task.auto_tags.map((tag, index) => <span key={index} className="inline-block px-2 py-1 bg-secondary text-white rounded-full text-xs" data-id="element-2110">
                      #{tag}
                    </span>)}
                </div>
              </CardContent>
            </Card>}
          {/* Technical Details Card */}
          {(task.resolution || task.width || task.height || task.vcodec || task.acodec) && <Card data-id="element-2111">
              <CardContent className="p-4" data-id="element-2112">
                <div className="flex justify-between items-center mb-3" data-id="element-2113">
                  <h3 className="font-medium flex items-center" data-id="element-2114">
                    <Monitor size={16} className="mr-2 text-gray-400" data-id="element-2115" />
                    Technical Details
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowTechnicalDetails(!showTechnicalDetails)} className="h-8 px-2 text-xs" data-id="element-2116">
                    {showTechnicalDetails ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showTechnicalDetails ? <div className="space-y-2 text-sm" data-id="element-2117">
                    {task.resolution && <div className="flex justify-between" data-id="element-2118">
                        <span className="text-gray-400" data-id="element-2119">Resolution</span>
                        <span data-id="element-2120">{task.resolution}</span>
                      </div>}
                    {(task.width || task.height) && <div className="flex justify-between" data-id="element-2121">
                        <span className="text-gray-400" data-id="element-2122">Dimensions</span>
                        <span data-id="element-2123">
                          {task.width || '?'}×{task.height || '?'}
                        </span>
                      </div>}
                    {task.aspect_ratio && <div className="flex justify-between" data-id="element-2124">
                        <span className="text-gray-400" data-id="element-2125">Aspect Ratio</span>
                        <span data-id="element-2126">{task.aspect_ratio.toFixed(2)}</span>
                      </div>}
                    {task.filesize && <div className="flex justify-between" data-id="element-2127">
                        <span className="text-gray-400" data-id="element-2128">File Size</span>
                        <span data-id="element-2129">
                          {(task.filesize / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      </div>}
                    {task.vcodec && <div className="flex justify-between" data-id="element-2130">
                        <span className="text-gray-400" data-id="element-2131">Video Codec</span>
                        <span data-id="element-2132">{task.vcodec}</span>
                      </div>}
                    {task.acodec && <div className="flex justify-between" data-id="element-2133">
                        <span className="text-gray-400" data-id="element-2134">Audio Codec</span>
                        <span data-id="element-2135">{task.acodec}</span>
                      </div>}
                    {task.format_id && <div className="flex justify-between" data-id="element-2136">
                        <span className="text-gray-400" data-id="element-2137">Format</span>
                        <span data-id="element-2138">{task.format_id}</span>
                      </div>}
                  </div> : <div className="text-center py-2 text-sm text-gray-400" data-id="element-2139">
                    Click "Show" to view technical specifications
                  </div>}
              </CardContent>
            </Card>}
          {/* Download Options Card */}
          <Card className="hidden md:block" data-id="element-2140">
            <CardContent className="p-4" data-id="element-2141">
              <h3 className="font-medium mb-3" data-id="element-2142">Download Transcript</h3>
              <div className="space-y-2" data-id="element-2143">
                <Button variant="outline" className="w-full justify-start rounded-full" onClick={handleDownload} disabled={!transcript} data-id="element-2144">
                  <FileText size={16} className="mr-2" data-id="element-2145" />
                  Download as TXT
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-2" data-id="element-2146">
          {/* New Insights Card with Quote and TLDR */}
          {hasInsights && <Card className="mb-4 md:mb-6" data-id="element-2147">
              <div className="flex items-center justify-between p-3 md:p-4 border-b border-border/40" data-id="element-2148">
                <h2 className="font-semibold text-sm md:text-base flex items-center" data-id="element-2149">
                  <BrainCircuit size={16} className="mr-2 text-primary" data-id="element-2150" />
                  Insights
                </h2>
                {task.quote && <div className="flex items-center space-x-1" data-id="element-2151">
                    <Button variant="ghost" size="sm" onClick={handleCopyQuote} className="h-8 px-2 text-xs rounded-full" data-id="element-2152">
                      {quoteCopied ? <Check size={14} className="mr-1" data-id="element-2153" /> : <Copy size={14} className="mr-1" data-id="element-2154" />}
                      {quoteCopied ? 'Copied!' : 'Copy Quote'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleShareQuote} className="h-8 px-2 text-xs rounded-full" data-id="element-2155">
                      <Twitter size={14} className="mr-1" data-id="element-2156" />
                      Share
                    </Button>
                  </div>}
              </div>
              <div className="p-4 md:p-6" data-id="element-2157">
                {/* Quote Section */}
                {task.quote && <div className="mb-6" data-id="element-2158">
                    <div className="flex items-center mb-2" data-id="element-2159">
                      <Quote size={16} className="mr-2 text-primary" data-id="element-2160" />
                      <h3 className="font-semibold text-sm" data-id="element-2161">Key Quote</h3>
                    </div>
                    <blockquote className="border-l-4 border-primary/30 pl-4 py-1 text-lg md:text-xl font-medium italic text-primary/90" data-id="element-2162">
                      "{task.quote}"
                    </blockquote>
                  </div>}
                {/* TLDR Section */}
                {tldrItems && tldrItems.length > 0 && <div data-id="element-2163">
                    <div className="flex items-center mb-3" data-id="element-2164">
                      <FileText size={16} className="mr-2 text-primary" data-id="element-2165" />
                      <h3 className="font-semibold text-sm" data-id="element-2166">TLDR</h3>
                    </div>
                    <ul className="space-y-2 pl-2" data-id="element-2167">
                      {tldrItems.map((item, index) => <li key={index} className="flex items-start" data-id="element-2168">
                          <span className="inline-block bg-primary/20 text-primary font-bold rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5" data-id="element-2169">
                            {index + 1}
                          </span>
                          <span className="text-sm md:text-base" data-id="element-2170">{item}</span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </Card>}
          {/* Description Card (if available) */}
          {task?.description && <Card className="mb-4 md:mb-6" data-id="element-2171">
              <div className="flex items-center justify-between p-3 md:p-4 border-b border-border/40" data-id="element-2172">
                <h2 className="font-semibold text-sm md:text-base flex items-center" data-id="element-2173">
                  <Info size={16} className="mr-2 text-primary" data-id="element-2174" />
                  Description
                </h2>
              </div>
              <div className="p-3 md:p-4" data-id="element-2175">
                <p className="text-sm whitespace-pre-wrap" data-id="element-2176">
                  {task.description}
                </p>
              </div>
            </Card>}
          {/* Transcript Card */}
          <Card className="mb-6" data-id="element-2177">
            <div className="flex items-center justify-between p-3 md:p-4 border-b border-border/40" data-id="element-2178">
              <h2 className="font-semibold text-sm md:text-base flex items-center" data-id="element-2179">
                <FileText size={16} className="mr-2 text-primary" data-id="element-2180" />
                Full Transcript
              </h2>
              <div className="flex items-center" data-id="element-2181">
                <Button variant="ghost" size="sm" onClick={() => setShowFullTranscript(!showFullTranscript)} className="text-primary h-9 px-2 rounded-full mr-1" data-id="element-2182">
                  {showFullTranscript ? <ChevronUp size={14} className="mr-1" data-id="element-2183" /> : <ChevronDown size={14} className="mr-1" data-id="element-2184" />}
                  {showFullTranscript ? 'Hide' : 'Show'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopyTranscript} className="text-primary h-9 px-2 rounded-full" disabled={!transcript} data-id="element-2185">
                  <Copy size={14} className="mr-1" data-id="element-2186" />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleShare} className="text-primary h-9 px-2 hidden md:flex rounded-full" disabled={!transcript} data-id="element-2187">
                  <Share2 size={14} className="mr-1" data-id="element-2188" />
                  Share
                </Button>
              </div>
            </div>
            {transcript && transcript.segments.length > 0 ? <>
                {!showFullTranscript ? <div className="p-6 text-center border-t border-border/10" data-id="element-2189">
                    <p className="text-sm text-muted-foreground mb-4" data-id="element-2190">
                      The full transcript contains {transcript.segments.length}{' '}
                      segments and approximately{' '}
                      {Math.round(transcript.text.length / 5)} words.
                    </p>
                    <Button variant="outline" onClick={() => setShowFullTranscript(true)} className="rounded-full" data-id="element-2191">
                      <ChevronDown size={16} className="mr-2" data-id="element-2192" />
                      Show Full Transcript
                    </Button>
                  </div> : <div className="p-4 md:p-6 max-h-[600px] md:max-h-[800px] overflow-y-auto" data-id="element-2193">
                    <div className="transcript-container" data-id="element-2194">
                      {transcript.segments.map(segment => <div key={segment.id} className="mb-4" data-id="element-2195">
                          <div className="text-xs text-gray-400 mb-1" data-id="element-2196">
                            <span data-id="element-2197">
                              {Math.floor(segment.start / 60)}:
                              {Math.floor(segment.start % 60).toString().padStart(2, '0')}{' '}
                              -{Math.floor(segment.end / 60)}:
                              {Math.floor(segment.end % 60).toString().padStart(2, '0')}
                            </span>
                          </div>
                          <p className="font-medium text-base md:text-lg" data-id="element-2198">
                            {segment.text}
                          </p>
                        </div>)}
                    </div>
                  </div>}
              </> : <div className="p-6 md:p-12 text-center" data-id="element-2199">
                <p className="text-gray-400" data-id="element-2200">
                  {transcript ? 'No transcript segments available.' : 'Transcript data not available.'}
                </p>
              </div>}
          </Card>
          {/* Share Transcript Card */}
          {task && task.task_id && <Card className="mb-6" data-id="element-2201">
              <CardContent className="p-4" data-id="element-2202">
                <ShareTranscript taskId={task.task_id} title={task.title || undefined} data-id="element-2203" />
              </CardContent>
            </Card>}

          {/* Research Tools Section - Added at the bottom */}
          {task && task.task_id && task.status === 'completed' && <ResearchTools taskId={task.task_id} videoId={task.video_id || undefined} data-id="element-2204" />}

          {/* Similar Transcriptions */}
          {similarTranscriptions.length > 0 && <Card className="mt-6" data-id="element-2205">
              <div className="flex items-center justify-between p-3 md:p-4 border-b border-border/40" data-id="element-2206">
                <h2 className="font-semibold text-sm md:text-base" data-id="element-2207">
                  Similar Content
                </h2>
              </div>
              <div className="p-3 md:p-4" data-id="element-2208">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2209">
                  {similarTranscriptions.map(transcription => <div key={transcription.task_id} className="h-full" data-id="element-2210">
                      <TranscriptionCard transcription={transcription} data-id="element-2211" />
                    </div>)}
                </div>
              </div>
            </Card>}
        </div>
      </div>
      {/* Mobile Download Button */}
      <div className="md:hidden mt-6 flex gap-3" data-id="element-2212">
        <Button variant="tiktok" className="flex-1 justify-center rounded-full" onClick={handleDownload} disabled={!transcript} data-id="element-2213">
          <FileText size={16} className="mr-2" data-id="element-2214" />
          Download as TXT
        </Button>
      </div>
    </div>;
}